"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { 
  Search, 
  Package, 
  ArrowRight, 
  Mail, 
  Hash,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useLookupOrderMutation } from "@/store"

export default function TrackOrderScreen() {
  const navigate = useNavigate()
  const [lookupOrder, { isLoading }] = useLookupOrderMutation()
  
  // Form states
  const [orderId, setOrderId] = useState("")
  const [email, setEmail] = useState("")
  const [accessToken, setAccessToken] = useState("")
  const [error, setError] = useState("")

  const handleLookupByDetails = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!orderId.trim() || !email.trim()) {
      setError("Please enter both order ID and email address")
      return
    }

    try {
      const result = await lookupOrder({ 
        displayOrderId: orderId.trim(), 
        email: email.trim().toLowerCase() 
      }).unwrap()

      if (result.success && result.data?.accessToken) {
        navigate(`/orders/${result.data.accessToken}`)
      } else {
        setError("Order not found. Please check your order ID and email.")
      }
    } catch (err: any) {
      setError(err.data?.message || "Order not found. Please check your details and try again.")
    }
  }

  const handleLookupByToken = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!accessToken.trim()) {
      setError("Please enter your order tracking link or token")
      return
    }

    // Extract token from URL if full URL was pasted
    let token = accessToken.trim()
    if (token.includes("/orders/")) {
      const match = token.match(/\/orders\/([a-zA-Z0-9-]+)/)
      if (match) {
        token = match[1]
      }
    }

    // Navigate directly to the order tracking page
    navigate(`/orders/${token}`)
  }

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="mb-6 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-6">
          <Package className="h-12 w-12 text-orange-500" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Track Your Order</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Enter your order details below to track your delivery status in real-time
        </p>
      </motion.div>

      {/* Tracking Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="max-w-xl mx-auto"
      >
        <Card>
          <CardHeader>
            <CardTitle>Find Your Order</CardTitle>
            <CardDescription>
              Use your order ID and email, or paste your tracking link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="details" className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  Order Details
                </TabsTrigger>
                <TabsTrigger value="link" className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Tracking Link
                </TabsTrigger>
              </TabsList>

              {/* Lookup by Order ID + Email */}
              <TabsContent value="details">
                <form onSubmit={handleLookupByDetails} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="orderId">Order ID</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="orderId"
                        type="text"
                        placeholder="e.g., 2001"
                        value={orderId}
                        onChange={(e) => setOrderId(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your order ID was sent to your email after placing the order
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The email address you used when placing the order
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white"
                    size="lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Track Order
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Lookup by Tracking Link/Token */}
              <TabsContent value="link">
                <form onSubmit={handleLookupByToken} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="accessToken">Tracking Link or Token</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="accessToken"
                        type="text"
                        placeholder="Paste your tracking link or token"
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Paste the full tracking URL or just the token from your order confirmation email
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white"
                    size="lg"
                    disabled={isLoading}
                  >
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Go to Order
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>

      {/* Info Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="max-w-3xl mx-auto mt-12"
      >
        <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="mb-4 inline-flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 p-4">
                <Hash className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Enter Order ID</h3>
              <p className="text-sm text-muted-foreground">
                Find your order ID in the confirmation email we sent you
              </p>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="mb-4 inline-flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 p-4">
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Real-Time Updates</h3>
              <p className="text-sm text-muted-foreground">
                Track your order status from confirmation to delivery
              </p>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="mb-4 inline-flex items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30 p-4">
                <CheckCircle2 className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Stay Informed</h3>
              <p className="text-sm text-muted-foreground">
                Know exactly when your delicious meal will arrive
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  )
}
